import * as THREE from 'three';
import { Vec3 } from '../core/vec';
import { PreviewBridge } from '../app/context';

/** Zeichnet Werkzeug-Vorschauen (Rubber-Band, Snap-Punkte, Flächen-Highlights). */
export class PreviewRenderer implements PreviewBridge {
  group = new THREE.Group();
  private objects: THREE.Object3D[] = [];
  private sprites: THREE.Sprite[] = [];

  private track<T extends THREE.Object3D>(o: T): T {
    this.objects.push(o);
    this.group.add(o);
    return o;
  }

  line(a: Vec3, b: Vec3, color: number, dashed = false): void {
    const geo = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(a.x, a.y, a.z), new THREE.Vector3(b.x, b.y, b.z)]);
    if (!dashed) {
      this.track(new THREE.Line(geo, new THREE.LineBasicMaterial({ color, depthTest: false })));
      return;
    }
    // Gepunktete Ableitungslinie (wie SketchUps Inferenz-Linien)
    const len = Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
    const dash = Math.max(0.01, Math.min(0.08, len / 30));
    const line = new THREE.Line(geo, new THREE.LineDashedMaterial({ color, depthTest: false, dashSize: dash, gapSize: dash }));
    line.computeLineDistances();
    this.track(line);
  }

  polyline(points: Vec3[], color: number, closed = false): void {
    if (points.length < 2) return;
    const pts = points.map((p) => new THREE.Vector3(p.x, p.y, p.z));
    if (closed) pts.push(pts[0]!.clone());
    const geo = new THREE.BufferGeometry().setFromPoints(pts);
    this.track(new THREE.Line(geo, new THREE.LineBasicMaterial({ color, depthTest: false })));
  }

  dot(p: Vec3, color: number, sizePx = 9): void {
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({ color, depthTest: false, sizeAttenuation: false })
    );
    sprite.position.set(p.x, p.y, p.z);
    sprite.scale.setScalar(sizePx / 600); // sizeAttenuation:false → Einheiten = NDC-Bruchteil
    sprite.renderOrder = 10;
    this.track(sprite);
    this.sprites.push(sprite);
  }

  faceOverlay(points: Vec3[], color: number): void {
    if (points.length < 3) return;
    const shape = new THREE.Shape(points.map((p) => new THREE.Vector2(p.x, p.y)));
    const geo = new THREE.ShapeGeometry(shape);
    const mesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.45, depthTest: false }));
    mesh.renderOrder = 5;
    this.track(mesh);
  }

  /** Geister-Quader (z. B. Möbel-Vorschau), von min-Ecke bis max-Ecke. */
  box(min: Vec3, max: Vec3, color: number): void {
    const w = max.x - min.x, d = max.y - min.y, h = max.z - min.z;
    if (w <= 1e-6 || d <= 1e-6 || h <= 1e-6) return;
    const geo = new THREE.BoxGeometry(w, d, h);
    const mesh = new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ color, transparent: true, opacity: 0.4, depthTest: false }));
    mesh.position.set((min.x + max.x) / 2, (min.y + max.y) / 2, (min.z + max.z) / 2);
    mesh.renderOrder = 4;
    this.track(mesh);
  }

  clear(): void {
    for (const o of this.objects) {
      this.group.remove(o);
      const anyO = o as unknown as { geometry?: THREE.BufferGeometry; material?: THREE.Material | THREE.Material[] };
      anyO.geometry?.dispose();
      const m = anyO.material;
      if (Array.isArray(m)) m.forEach((x) => x.dispose());
      else m?.dispose();
    }
    this.objects.length = 0;
    this.sprites.length = 0;
  }
}

export const AXIS_COLORS = [0xd23c3c, 0x2fa04a, 0x3c6fd2] as const; // X rot, Y grün, Z blau
export const SNAP_COLORS: Record<string, number> = {
  endpoint: 0x2fa04a,
  midpoint: 0x00b7c7,
  onEdge: 0xd23c3c,
  onFace: 0x3c6fd2,
  onGuide: 0x9a68d8,
  grid: 0x7a8a98,
  origin: 0x1f2933,
  ground: 0x888888
};
